import { UseTrackingMappedMedia } from '../../plugins/Tracking';
import { AdBeginEventArgs, AdBreakBeginEventArgs } from '../../core/typedEvents';
import type { PlayUnifiedLiveTrackingQuery } from '../dataProviders/__generated__/Tracking';

export enum UnifiedTrackingMachineEventTypes {
  Load = 'load',
  TimeUpdate = 'timeupdate',
  NewTrackingEntries = 'newEpgEntries',
  Seeking = 'seeking',
  AdBreakBegin = 'ad.breakbegin',
  AdBreakEnd = 'ad.breakend',
  AdBegin = 'ad.begin',
  AdEnd = 'ad.end',
  Stop = 'stop',
  Pause = 'pause',
  Buffering = 'buffering',
  Play = 'play',
  Destroy = 'destroy',
}

export abstract class UnifiedTrackingMachine {
  send(event: UnifiedTrackingMachineEvent) {
    switch (event.type) {
      case UnifiedTrackingMachineEventTypes.Load:
        this.onLoad(event);
        break;
      case UnifiedTrackingMachineEventTypes.TimeUpdate:
        this.onTimeUpdate(event);
        break;
      case UnifiedTrackingMachineEventTypes.NewTrackingEntries:
        this.onNewTrackingEntries(event);
        break;
      case UnifiedTrackingMachineEventTypes.Seeking:
        this.onSeeking();
        break;
      case UnifiedTrackingMachineEventTypes.AdBreakBegin:
        this.onAdBreakBegin(event);
        break;
      case UnifiedTrackingMachineEventTypes.AdBreakEnd:
        this.onAdBreakEnd();
        break;
      case UnifiedTrackingMachineEventTypes.AdBegin:
        this.onAdBegin(event);
        break;
      case UnifiedTrackingMachineEventTypes.AdEnd:
        this.onAdEnd();
        break;
      case UnifiedTrackingMachineEventTypes.Stop:
        this.onStop();
        break;
      case UnifiedTrackingMachineEventTypes.Pause:
        this.onPause();
        break;
      case UnifiedTrackingMachineEventTypes.Buffering:
        this.onBuffering();
        break;
      case UnifiedTrackingMachineEventTypes.Destroy:
        this.onDestroy();
    }
  }

  protected abstract onDestroy(): void;

  protected abstract onBuffering(): void;

  protected abstract onPause(): void;

  protected abstract onStop(): void;

  protected abstract onAdEnd(): void;

  protected abstract onAdBegin(event: UnifiedTrackingMachineAdBeginEvent): void;

  protected abstract onAdBreakEnd(): void;

  protected abstract onAdBreakBegin(event: UnifiedTrackingMachineAdBreakBeginEvent): void;

  protected abstract onSeeking(): void;

  protected abstract onNewTrackingEntries(
    event: UnifiedTrackingMachineNewTrackingEntriesEvent
  ): void;

  protected abstract onTimeUpdate(event: UnifiedTrackingMachineTimeUpdateEvent): void;

  protected abstract onLoad(event: UnifiedTrackingMachineLoadEvent): void;
}

export type UnifiedTrackingMachineGenericEvent<T extends UnifiedTrackingMachineEventTypes> = {
  type: T;
};
export type UnifiedTrackingMachineLoadEvent =
  UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.Load> & {
    value: {
      media: UseTrackingMappedMedia;
      currentTime: number;
      subtitlesAvailable: boolean | undefined;
      subtitlesEnabled: boolean | undefined;
    };
  };
export type UnifiedTrackingMachineTimeUpdateEvent =
  UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.TimeUpdate> & {
    value: {
      currentTime: number;
      currentBitrate: number;
      droppedFrames: number;
    };
  };
export type UnifiedTrackingMachineNewTrackingEntriesEvent =
  UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.NewTrackingEntries> & {
    value: PlayUnifiedLiveTrackingQuery;
  };
export type UnifiedTrackingMachineAdBreakBeginEvent =
  UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.AdBreakBegin> & {
    value: AdBreakBeginEventArgs;
  };
export type UnifiedTrackingMachineAdBeginEvent =
  UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.AdBegin> & {
    value: AdBeginEventArgs;
  };
export type UnifiedTrackingMachineEvent =
  | UnifiedTrackingMachineLoadEvent
  | UnifiedTrackingMachineTimeUpdateEvent
  | UnifiedTrackingMachineNewTrackingEntriesEvent
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.AdBreakEnd>
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.Seeking>
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.AdEnd>
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.Stop>
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.Pause>
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.Buffering>
  | UnifiedTrackingMachineGenericEvent<UnifiedTrackingMachineEventTypes.Destroy>
  | UnifiedTrackingMachineAdBreakBeginEvent
  | UnifiedTrackingMachineAdBeginEvent;
